import type { IFilter } from "@/api/AppApi/services/filters/interfaces/IGetFilters";
import type { IAttiribs } from "./interfaces";
import type { ICommonProductDetails } from "@/api/AppApi/services/product/interfaces/api-product-details-interface";
import { ICartItemType } from "@/api/AppApi/services/cart/interfaces/cart-interfaces";

export function fillSelectedAttributes(
  attributes: IFilter[],
  startIndex = 0,
  unavailableKeysMap?: IAttiribs,
  outOfStockKeysMap?: IAttiribs,
) {
  const selectedKeys: string[] = [];
  let currentIndex = startIndex;

  const attributesToStartFrom = attributes.filter(
    (_, index) => index >= startIndex,
  );

  for (const attribute of attributesToStartFrom) {
    const unavailableKeys =
      unavailableKeysMap?.[attribute.configurations.key] || [];
    const outOfStockKeys =
      outOfStockKeysMap?.[attribute.configurations.key] || [];

    if (attribute.data?.length === 1) {
      selectedKeys.push(attribute.data[0].key.toString());
      currentIndex += 1;
    } else if (
      attribute.data?.filter(
        (item) =>
          !unavailableKeys.includes(item.key) &&
          !outOfStockKeys.includes(item.key),
      ).length === 1
    ) {
      // Find the one available and in-stock option
      const availableOption = attribute.data.find(
        (item) =>
          !unavailableKeys.includes(item.key) &&
          !outOfStockKeys.includes(item.key),
      );
      if (availableOption) {
        selectedKeys.push(availableOption.key.toString());
        currentIndex += 1;
      } else {
        break;
      }
    } else {
      break;
    }
  }

  return { selectedKeys, currentIndex };
}

export function isAttributesCombinationAvailable(
  attributesCombination: string[],
  lakeMatrix: number[][],
) {
  return lakeMatrix.some((lakeRow) => {
    return attributesCombination.every((attribute) =>
      lakeRow.includes(+attribute),
    );
  });
}

export function findUnavailableAttributes(
  selectedAttributes: IAttiribs,
  product: ICommonProductDetails,
) {
  const attributesCombination = Object.values(selectedAttributes).reduce(
    (prev, curr) => [...prev, ...curr],
    [],
  );
  const { children, attributes_builder } = product;
  const childrenConvertedToAttrCombination = children.map(
    (child) => child.attribute_ids,
  );

  return attributes_builder
    .filter((_, attrIdx) => attrIdx > 0)
    .reduce((prev, curr) => {
      return {
        ...prev,
        [curr.configurations.key]: curr.data
          ?.filter(
            (attrVal) =>
              !isAttributesCombinationAvailable(
                [
                  ...attributesCombination.filter(
                    (item) =>
                      !curr.data?.map((item) => item.key).includes(item),
                  ),
                  attrVal.key,
                ],
                childrenConvertedToAttrCombination,
              ),
          )
          .map((item) => item.key),
      };
    }, {});
}

export function findOutOfStockAttributes(
  selectedAttributes: IAttiribs,
  product: ICommonProductDetails,
  cartItems: ICartItemType[] | undefined,
) {
  const { children, attributes_builder } = product;
  return attributes_builder.reduce((prev, curr) => {
    // For each attribute value, check if it should be marked as out of stock
    const outOfStockKeys = curr.data
      ?.filter((attrVal) => {
        // Get all children that have this attribute value
        const childrenWithThisAttr = children.filter((child) =>
          child.attribute_ids.includes(+attrVal.key),
        );

        // Get all selected attributes except the current attribute being checked
        const otherSelectedAttrs = Object.entries(selectedAttributes)
          .filter(([key]) => key !== curr.configurations.key)
          .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

        // If there are other selected attributes, filter children that match those selections
        let filteredChildren = childrenWithThisAttr;
        if (Object.keys(otherSelectedAttrs).length > 0) {
          filteredChildren = childrenWithThisAttr.filter((child) => {
            return Object.entries(otherSelectedAttrs).every(
              ([attrKey, attrValues]) => {
                // Find the attribute in attributes_builder
                const attribute = attributes_builder.find(
                  (attr) => attr.configurations.key === attrKey,
                );
                if (!attribute) return true;

                // Check if the child has any of the selected attribute values
                return (attrValues as Array<string>).some((val) => {
                  const attrData = attribute.data?.find((d) => d.key === val);
                  return (
                    attrData && child.attribute_ids.includes(+attrData.key)
                  );
                });
              },
            );
          });
        }

        // If we're in the initial state (no selections), don't mark first-level attributes as out of stock
        if (
          Object.keys(selectedAttributes).length === 0 &&
          attributes_builder.indexOf(curr) === 0
        ) {
          return false;
        }

        // Mark as out of stock only if ALL children with this attribute (and matching other selections) are out of stock
        // AND there is at least one child with this attribute
        return (
          filteredChildren.length > 0 &&
          filteredChildren.every((child) => {
            const currentProductQuantityInCart =
              cartItems
                ?.filter((item) => item.inner_id === child.id)
                .reduce((prev, current) => prev + current.quantity, 0) ?? 0;
            const outOfStock =
              child?.quantity_on_hand - currentProductQuantityInCart <= 0 ||
              (!!child.min_quantity_in_cart &&
                child.quantity_on_hand - currentProductQuantityInCart <
                  child.min_quantity_in_cart);
            return outOfStock;
          })
        );
      })
      .map((item) => item.key);

    return {
      ...prev,
      [curr.configurations.key]: outOfStockKeys || [],
    };
  }, {});
}
